import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Box
} from '@mui/material';
import {
  BadgeOutlined,
  Close,
  DevicesOutlined,
  LanOutlined,
  PersonAddAlt1Outlined,
} from '@mui/icons-material';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import { useUserContext } from '../../context/user';
import { useAuthContext } from '../../context/auth';
import { ROLES } from '../../config/roles';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import AvatarEditor from '../profile/AvatarEditor';

const PICKABLE_ROLES = ['User', 'Admin', 'Guest'];

const Add = ({ units } ) => {
  const theme = useTheme();
  const { paperSx, titleSx, contentSx, actionsSx, fieldSx, menuProps, goldButtonSx, cancelButtonSx } = useDialogStyles();

  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useUserContext();
  const { auth } = useAuthContext();

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const nameRef = useRef('');
  const emailRef = useRef('');
  const passwordRef = useRef('');
  const deviceIdRef = useRef('');
  const [active, setActive] = useState(false);

  const [selectedRole, setSelectedRole] = useState('User');
  const [previewName, setPreviewName] = useState('');
  const [previewEmail, setPreviewEmail] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);   // cropped picture, uploaded once the user exists
  const [openCount, setOpenCount] = useState(0);        // gives the avatar editor a fresh start on every open
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [selectedRegiment, setSelectedRegiment] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedOutpost, setSelectedOutpost] = useState(null);
  const [commands, setCommands] = useState([]);
  const [regiments, setRegiments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [outspots, setOutposts] = useState([]);

  const permitPickRole = Boolean(auth?.roles?.includes(ROLES.Root));

  // ---------- Theme (same look as the Users table and Edit.js) ----------
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const accentText = theme.palette.getContrastText(accent);
  const muted = theme.palette.text.secondary;
  const role = permitPickRole ? selectedRole : 'User';

  // Role chip exactly like the Role column of the Users table
  const roleChipSx = (r) => ({
    height: 22,
    fontWeight: 600,
    fontSize: '0.72rem',
    color: r === ROLES.Root ? accentText : accent,
    borderColor: alpha(accent, 0.5),
    backgroundColor: r === ROLES.Root ? accent : alpha(accent, 0.08),
  });

  // ---------- Logic ----------
  const openDialog = () => {
    setPreviewName('');
    setPreviewEmail('');
    setAvatarFile(null);
    setActive(false);
    setOpenCount((n) => n + 1);
    setShow(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setShow(false);
    setError(null);
  };

  // The new user's id is needed for the picture, so it goes up right after the user is created
  const uploadAvatar = async (userId) => {
    if (!avatarFile) return null;
    try {
      const form = new FormData();
      form.append('avatar', avatarFile, 'avatar.jpg');
      const { data } = await axiosPrivate.put(`/api/users/${userId}/avatar`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.avatar;
    } catch (err) {
      console.error('User created, but the picture could not be uploaded', err);
      return null;
    }
  };

  const handleShowPassword = () => setShowPassword((prev) => !prev);

  const handleAdd = async () => {
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    const addUser = {
      name: nameRef.current.value,
      email: emailRef.current.value,
      password: passwordRef.current.value,
      roles: [permitPickRole ? selectedRole : undefined],
      active,
      commandId: selectedCommand==='0' ? null : selectedCommand,
      regimentId: selectedRegiment==='0' ? null : selectedRegiment,
      companyId: selectedCompany==='0' ? null : selectedCompany,
      outpostId: selectedOutpost==='0' ? null : selectedOutpost,
      deviceId: deviceIdRef.current.value,
    };

    setSaving(true);
    try {
      const response = await axiosPrivate.post('/api/users', addUser);
      const avatar = await uploadAvatar(response.data._id);
      dispatch({ type: 'CREATE_USER', payload: { ...response.data, avatar } });
      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || 'Unable to create the user');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setCommands(units.filter(unit => unit.type === 0));
    setRegiments(units.filter(unit => unit.type === 1));
    setCompanies(units.filter(unit => unit.type === 2));
    setOutposts(units.filter(unit => unit.type === 3));
  },[units])

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // One unit drop-down (a function, not a component: a component defined here would restart on every render)
  const renderUnitSelect = (label, value, setValue, items) => (
    <FormControl fullWidth size="small" sx={fieldSx}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || ''} onChange={(e) => setValue(e.target.value)} MenuProps={menuProps}>
        <MenuItem value="0">All</MenuItem>
        {items?.map((item) => (
          <MenuItem key={item._id} value={item._id}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Small section heading inside the form
  const section = (Icon, title, hint) => (
    <Box display="flex" alignItems="center" gap={1} sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
      <Icon sx={{ fontSize: 18, color: accent }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: muted }}>
          · {hint}
        </Typography>
      )}
      <Box flex={1} sx={{ borderTop: `1px solid ${theme.palette.divider}`, ml: 1 }} />
    </Box>
  );

  return (
    <>
      {/* Same height and corners as the search box beside it; gold like the rest of the page */}
      <Button
        onClick={openDialog}
        startIcon={<PersonAddAlt1Outlined />}
        sx={{
          ...goldButtonSx,
          height: 36,
          px: 2,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.875rem',
          whiteSpace: 'nowrap',
        }}
      >
        {isMobile ? "Add" : "Add User"}
      </Button>

      <Dialog open={show} onClose={closeDialog} fullWidth maxWidth="md" PaperProps={{ sx: paperSx }}>
        <DialogTitle sx={{ ...titleSx, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, 0.12),
              border: `1px solid ${alpha(accent, 0.4)}`,
              flexShrink: 0,
            }}
          >
            <PersonAddAlt1Outlined sx={{ fontSize: 20 }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>New user</Typography>
            <Typography variant="caption" sx={{ color: muted }}>
              Create an account and assign it to a unit
            </Typography>
          </Box>
          <IconButton onClick={closeDialog} disabled={saving} aria-label="Close" sx={{ color: muted }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={contentSx}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '230px 1fr' },
              gap: 3,
              alignItems: 'stretch',      // the profile card runs as tall as the form
            }}
          >
            {/* ===== Left: live preview of the new user ===== */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.75,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : alpha(accent, 0.05),
              }}
            >
              <AvatarEditor key={openCount} user={{ name: previewName }} onFile={setAvatarFile} menu />

              <Box textAlign="center" width="100%" minWidth={0}>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{ fontWeight: 700, color: previewName ? 'text.primary' : 'text.secondary' }}
                >
                  {previewName || 'New user'}
                </Typography>
                <Typography variant="body2" noWrap color="text.secondary">
                  {previewEmail || 'No email yet'}
                </Typography>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center">
                <Chip size="small" variant="outlined" label={role} sx={roleChipSx(role)} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={active ? 'Active' : 'Inactive'}
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    color: active ? theme.palette.success.main : muted,
                    borderColor: active ? alpha(theme.palette.success.main, 0.5) : theme.palette.divider,
                  }}
                />
              </Box>

              <Typography variant="caption" sx={{ color: muted, textAlign: 'center' }}>
                Click the picture to add a photo
              </Typography>
            </Box>

            {/* ===== Right: details ===== */}
            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignContent: 'start' }}>
              {section(BadgeOutlined, 'Account')}

              <TextField
                fullWidth
                size="small"
                label="Name"
                inputRef={nameRef}
                onChange={(e) => setPreviewName(e.target.value)}
                autoComplete="off"
                autoFocus
                sx={fieldSx}
              />

              <TextField
                fullWidth
                size="small"
                label="Email"
                inputRef={emailRef}
                onChange={(e) => setPreviewEmail(e.target.value)}
                autoComplete="off"
                sx={fieldSx}
              />

              <TextField
                fullWidth
                size="small"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                inputRef={passwordRef}
                autoComplete="new-password"          // stops the browser from filling in a saved password
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={handleShowPassword}
                        aria-label="Show or hide the password"
                        sx={{ color: muted }}
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldSx, gridColumn: { sm: permitPickRole ? 'auto' : '1 / -1' } }}
              />

              {permitPickRole && (
                <Box>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={selectedRole}
                    onChange={(e, v) => { if (v) setSelectedRole(v); }}
                    aria-label="Role"
                    sx={{
                      height: 40,
                      p: 0.375,
                      gap: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300]}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        borderRadius: '6px !important',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        color: muted,
                        '&:hover': { backgroundColor: alpha(accent, 0.08) },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          color: accent,
                          backgroundColor: alpha(accent, 0.14),
                          boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
                        },
                      },
                    }}
                  >
                    {PICKABLE_ROLES.map((r) => (
                      <ToggleButton key={r} value={r}>{r}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {section(LanOutlined, 'Assignment', '"All" = not limited to one unit')}

              {renderUnitSelect('Command', selectedCommand, setSelectedCommand, commands)}
              {renderUnitSelect('Regiment', selectedRegiment, setSelectedRegiment, regiments)}
              {renderUnitSelect('Company', selectedCompany, setSelectedCompany, companies)}
              {renderUnitSelect('Outpost', selectedOutpost, setSelectedOutpost, outspots)}

              {section(DevicesOutlined, 'Device & access')}

              <TextField
                fullWidth
                size="small"
                label="Device ID"
                inputRef={deviceIdRef}
                autoComplete="off"
                sx={fieldSx}
              />

              {/* Active: switch with what it means */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{
                  px: 1.5,
                  minHeight: 40,
                  borderRadius: 1.5,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300]}`,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
                }}
              >
                <Box minWidth={0}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>Active</Typography>
                  <Typography variant="caption" sx={{ color: muted }}>
                    {active ? 'Can sign in right away' : 'Cannot sign in until activated'}
                  </Typography>
                </Box>
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  inputProps={{ 'aria-label': 'Active' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accent },
                  }}
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={actionsSx}>
          <Button onClick={closeDialog} disabled={saving} sx={cancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1Outlined />}
            sx={{ ...goldButtonSx, textTransform: 'none', '&.Mui-disabled': { backgroundColor: alpha(accent, 0.5), color: accentText } }}
          >
            {saving ? 'Creating…' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Add;
